import React, { useState, useEffect } from 'react';
import { Receipt, FileText, Download, Calendar, Eye, Package, ShoppingCart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../components/AlertProvider';
import { API_BASE_URL } from '../../services/api';

interface InvoiceItem {
  id: number;
  product_title: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface Invoice {
  id: number;
  invoice_number: string;
  order_number?: string;
  status: string;
  total: number;
  issue_date: string;
  due_date: string;
  customer_name: string;
  customer_email: string;
  payment_label?: string;
  percentage: number;
  items?: InvoiceItem[];
}

const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300' },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' },
  overdue: { label: 'En retard', color: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300' },
  cancelled: { label: 'Annulée', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};

const BuyerInvoicesTailwind: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const { token } = useAuth();
  const { showError } = useAlert();

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!token) {
        showError('Non authentifié');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/v1/clients/me/invoices', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 404) {
          // 404 means no invoices found, not an error - show empty state
          setInvoices([]);
        } else if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        } else {
          const data = await response.json();
          setInvoices(data);
        }
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Erreur lors du chargement des factures');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [token]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    if (!token) return;
    setViewingInvoice(invoice);
    setPdfUrl(`${API_BASE_URL}/clients/me/invoices/${invoice.id}/pdf?token=${encodeURIComponent(token)}`);
  };

  const handleDownloadPDF = async (invoiceId: number) => {
    if (!token) return;

    try {
      // Use direct URL with token for download
      const downloadUrl = `${API_BASE_URL}/clients/me/invoices/${invoiceId}/pdf?token=${encodeURIComponent(token)}`;

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `facture-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erreur lors du téléchargement:', err);
    }
  };

  const handleBackToList = () => {
    setPdfUrl(null);
    setViewingInvoice(null);
  };


  const totalAmount = invoices.reduce((sum, inv) => sum + inv.total, 0);

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-green-50/20 to-slate-50 dark:from-slate-950 dark:via-green-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex flex-col gap-6 px-6 py-6 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-sm z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 via-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg transform transition-transform hover:scale-105">
              <Receipt className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Mes Factures</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                Gérez vos documents financiers
              </p>
            </div>
          </div>

          {/* Summary Stats */}
          {!loading && invoices.length > 0 && (
            <div className="flex gap-4">
              <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700">
                <div className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Factures</div>
                <div className="text-lg font-bold text-slate-900 dark:text-slate-100">{invoices.length}</div>
              </div>
              <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700">
                <div className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Total à payer</div>
                <div className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(totalAmount)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
        {viewingInvoice ? (
          /* Vue détaillée avec PDF */
          <div className="h-full flex gap-6 max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-300">
            {/* Panneau gauche - Informations */}
            <div className="w-96 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-xl flex flex-col">
              {/* Bouton retour */}
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                <button
                  onClick={handleBackToList}
                  className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors px-3 py-2 rounded-lg hover:bg-white dark:hover:bg-slate-800"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span className="font-medium">Retour à la liste</span>
                </button>
              </div>

              {/* Informations de la facture */}
              <div className="p-6 space-y-8 overflow-y-auto flex-1">
                {/* En-tête */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      <Receipt className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                        {viewingInvoice.invoice_number}
                      </h2>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        Référence client
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${statusConfig[viewingInvoice.status]?.color || 'bg-gray-100 text-gray-800 border-gray-200'} shadow-sm`}>
                      {statusConfig[viewingInvoice.status]?.label || viewingInvoice.status}
                    </span>
                    {viewingInvoice.order_number && (
                      <div className="flex items-center gap-1.5 px-3 py-1 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800/50 text-xs font-semibold rounded-full shadow-sm">
                        <ShoppingCart className="h-3 w-3" />
                        <span>{viewingInvoice.order_number}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Montants */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30 rounded-xl p-5 border border-slate-200 dark:border-slate-700/50 shadow-inner">
                  <div className="text-sm text-slate-500 dark:text-slate-400 mb-1 font-medium">Montant total TTC</div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                    {formatCurrency(viewingInvoice.total)}
                  </div>
                  {viewingInvoice.percentage < 100 && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded w-fit">
                      <span>•</span>
                      {viewingInvoice.percentage}% du bon de commande
                    </div>
                  )}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-medium uppercase">Émission</div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-900 dark:text-slate-100 font-semibold">{formatDate(viewingInvoice.issue_date)}</span>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-medium uppercase">Échéance</div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-900 dark:text-slate-100 font-semibold">{formatDate(viewingInvoice.due_date)}</span>
                    </div>
                  </div>
                </div>

                {/* Détails des articles */}
                {viewingInvoice.items && viewingInvoice.items.length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm uppercase text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2 tracking-wider">
                      <Package className="h-4 w-4" />
                      Détails
                    </h3>
                    <div className="space-y-3">
                      {viewingInvoice.items.map((item) => (
                        <div key={item.id} className="group bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors shadow-sm">
                          <div className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                            {item.product_title}
                          </div>
                          <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                            <span>{item.quantity} × {formatCurrency(item.unit_price)}</span>
                            <span className="font-bold text-slate-900 dark:text-slate-100">
                              {formatCurrency(item.line_total)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 mt-auto">
                  <button
                    onClick={() => handleDownloadPDF(viewingInvoice.id)}
                    className="w-full group flex items-center justify-center gap-2 px-4 py-3.5 bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-white text-white dark:text-slate-900 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl active:scale-[0.98]"
                  >
                    <Download className="h-5 w-5 group-hover:-translate-y-0.5 transition-transform" />
                    Télécharger la facture
                  </button>
                </div>
              </div>
            </div>

            {/* Panneau droit - PDF Viewer */}
            <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                <h3 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Aperçu du document
                </h3>
                <a
                  href={pdfUrl || '#'}
                  download
                  className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  title="Télécharger"
                  aria-label="Télécharger le PDF"
                >
                  <Download className="h-5 w-5" />
                </a>
              </div>
              <div className="flex-1 bg-slate-100 dark:bg-slate-950 flex items-center justify-center relative">
                {pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    className="w-full h-full"
                    title="Facture PDF"
                  />
                ) : (
                  <div className="text-center p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-green-600 mx-auto mb-4"></div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">Génération de l'aperçu...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-green-600 mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400 font-medium">Chargement des factures...</p>
            </div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <FileText className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3 tracking-tight">
                Aucune facture disponible
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                Vous n'avez aucune facture pour le moment. Vos futures factures apparaîtront ici dès leur émission.
              </p>
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-xl">
                <span className="text-xl">💡</span>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Besoin d'aide ? Contactez votre agent dédié.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto space-y-4">
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg hover:border-green-200 dark:hover:border-green-800/50 transition-all duration-300 overflow-hidden"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center p-5 gap-6">
                  {/* Icon & Info */}
                  <div className="flex items-center gap-5 min-w-[280px]">
                    <div className="h-12 w-12 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Receipt className="h-6 w-6 text-slate-400 group-hover:text-green-600 transition-colors" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 font-mono tracking-tight group-hover:text-green-600 transition-colors">
                        {invoice.invoice_number}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusConfig[invoice.status]?.color || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                          {statusConfig[invoice.status]?.label || invoice.status}
                        </span>
                        {invoice.order_number && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                            <ShoppingCart className="h-3 w-3" />
                            {invoice.order_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Dates - Hidden on mobile, shown on md+ */}
                  <div className="hidden md:flex flex-1 items-center justify-center gap-8 border-l border-r border-slate-100 dark:border-slate-800 px-8">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Émission</span>
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        {formatDate(invoice.issue_date)}
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Échéance</span>
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        {formatDate(invoice.due_date)}
                      </div>
                    </div>
                  </div>

                  {/* Amount & Actions */}
                  <div className="flex items-center justify-between w-full md:w-auto gap-8 ml-auto">
                    <div className="text-right">
                      <span className="block text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">Montant TTC</span>
                      <span className="text-xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                        {formatCurrency(invoice.total)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className="p-2.5 text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-xl transition-all"
                        title="Consulter"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDownloadPDF(invoice.id)}
                        className="p-2.5 text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-all"
                        title="Télécharger"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mobile Dates (shown only on small screens) */}
                <div className="md:hidden flex items-center justify-between px-5 pb-5 pt-0 text-sm border-t border-slate-100 dark:border-slate-800 mt-2 pt-3">
                  <div className="text-slate-500">
                    <span className="text-xs text-slate-400 block">Émission</span>
                    {formatDate(invoice.issue_date)}
                  </div>
                  <div className="text-slate-500 text-right">
                    <span className="text-xs text-slate-400 block">Échéance</span>
                    {formatDate(invoice.due_date)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerInvoicesTailwind;
